import {
  GenerationCatalogPagination,
  useGenerationCatalogPage,
} from './GenerationCatalogPagination.js';
import {
  GenerationPreconditions,
  type GenerationCredentialContext,
} from './GenerationCaseText.js';
import { useQuery } from '@tanstack/react-query';
import { GenerationVersionBadge } from './GenerationVersionBadge.js';
import {
  CircleAlertIcon,
  ChevronDownIcon,
  CheckCircle2Icon,
  ListChecksIcon,
  MinusCircleIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { useMemo, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import {
  QualityApiError,
  runProposalsQueryOptions,
  useRetryGeneratedCasePublication,
} from '@/lib/api/quality.js';
import type {
  GeneratedCasePublication,
  ProposalCase,
  PublicationRelationState,
  StoryLinkingProposalMeta,
} from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';

import { CaseCoverage } from './CaseCoverage.js';
import { GenerationCaseSteps } from './GenerationCaseSteps.js';
import { GenerationMetaReview } from './GenerationMetaReview.js';

const storyLinkingAlertStatuses = new Set<StoryLinkingProposalMeta['status']>([
  'skipped_missing_jira',
  'skipped_unsupported',
  'discovery_failed',
]);
const retryableRelationStates = new Set<PublicationRelationState>([
  'pending',
  'failed',
  'ambiguous',
]);
const incompletePlacementStates = new Set<
  GeneratedCasePublication['placement_state']
>(['failed', 'ambiguous']);

/** The created-case id's display prefix, per source: TestRail ids are numeric ("C42"), Azure work
 *  item ids are numeric too but read as "#42" (ADO convention), and Zephyr/Xray ids and Quality keys
 *  are already full keys ("ABC-T1", "Q-12") that need no prefix. Absent/unknown source falls back to TestRail's, matching
 *  every run persisted before the source was threaded through. */
function createdIdPrefix(source?: string | null): string {
  if (source === 'azure') return '#';
  if (source === 'zephyr' || source === 'xray' || source === 'own-tms')
    return '';
  return 'C';
}

function publicationIdentity(
  publication: GeneratedCasePublication,
  source?: string | null,
): string | null {
  const identity =
    publication.external_case_key ?? publication.external_case_id;
  if (!identity) return null;
  const prefix = createdIdPrefix(source ?? publication.provider);
  if (prefix && identity.startsWith(prefix)) return identity;
  return `${prefix}${identity}`;
}

function relationLabel(state: PublicationRelationState): string {
  switch (state) {
    case 'succeeded':
      return 'linked';
    case 'failed':
      return 'failed';
    case 'ambiguous':
      return 'uncertain';
    case 'skipped':
      return 'skipped';
    case 'pending':
      return 'pending';
  }
}

function placementLabel(
  state: GeneratedCasePublication['placement_state'],
): string {
  switch (state) {
    case 'succeeded':
      return 'Succeeded';
    case 'failed':
      return 'Failed';
    case 'ambiguous':
      return 'Uncertain';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Pending';
  }
}

/**
 * `'ambiguous'` collapses onto the same label as `'failed'`: the connector's response did not
 * confirm the write landed, so it is a failure to act on, not a third, distinct state — a
 * duplicate-write warning that visually reads as a milder "yellow" state is the one thing the
 * global no-uncounted-success rule exists to prevent. `caseStateBadgeVariant` below makes the same
 * collapse for the badge's color.
 */
function caseStateLabel(state: GeneratedCasePublication['case_state']): string {
  switch (state) {
    case 'created':
      return 'Created';
    case 'failed':
    case 'ambiguous':
      return 'Creation failed';
    case 'skipped_existing':
      return 'Skipped — already exists';
    case 'pending':
      return 'Pending';
  }
}

/** See `caseStateLabel` above — `'ambiguous'` collapses onto `'failed'` here too. */
function caseStateBadgeVariant(
  state: GeneratedCasePublication['case_state'],
): 'success' | 'destructive' | 'info' | 'secondary' {
  switch (state) {
    case 'created':
      return 'success';
    case 'failed':
    case 'ambiguous':
      return 'destructive';
    case 'skipped_existing':
      return 'info';
    case 'pending':
      return 'secondary';
  }
}

function hasPublicationIdentity(
  publication: GeneratedCasePublication,
): boolean {
  return Boolean(publication.external_case_id || publication.external_case_key);
}

function canRetryPublication(publication: GeneratedCasePublication): boolean {
  const caseCanBeRetried =
    publication.case_state === 'created' ||
    (publication.case_state === 'ambiguous' &&
      publication.provider === 'zephyr');
  return Boolean(
    hasPublicationIdentity(publication) &&
    caseCanBeRetried &&
    publication.relation_outcomes.some((outcome) =>
      retryableRelationStates.has(outcome.state),
    ),
  );
}

function isPartialSuccess(publication: GeneratedCasePublication): boolean {
  return Boolean(
    publication.case_state === 'created' &&
    (incompletePlacementStates.has(publication.placement_state) ||
      publication.relation_outcomes.some(
        (outcome) => outcome.state !== 'succeeded',
      )),
  );
}

function storyLinkingAlertMessage(
  storyLinking?: StoryLinkingProposalMeta | null,
): string | undefined {
  if (!storyLinking) return undefined;
  if (storyLinking.alert?.code === 'story_linking_local_destination')
    return undefined;
  if (!storyLinkingAlertStatuses.has(storyLinking.status)) return undefined;
  return storyLinking.alert?.message;
}

// Read-only result view for a finalized 'generation' run: the approved test cases with their steps,
// each marked created or not, in the target source's own id shape. Shown after the reviewer approves
// (the editable review panel is gone by then). Renders nothing until an approved version exists.
export function GeneratedCasesResult({
  runId,
  source,
  showGenerationMetadata = true,
  hideEmpty = false,
}: {
  runId: string;
  /** The run's OUTPUT test-management system ('testrail' | 'zephyr' | 'azure' | 'xray') — where the cases
   *  were written — picks the created-id prefix. For a generation run this can differ from the
   *  run's input source, so callers pass runOutputSource(run), not run.source. */
  source?: string | null;
  showGenerationMetadata?: boolean;
  hideEmpty?: boolean;
}): JSX.Element | null {
  const {
    data: proposals = [],
    isLoading,
    isError,
  } = useQuery(runProposalsQueryOptions(runId));

  const approved = useMemo(
    () => proposals.find((p) => p.status === 'approved'),
    [proposals],
  );
  const retry = useRetryGeneratedCasePublication(runId);
  const catalogIndices = approved?.publications?.length
    ? approved.publications.map((item) => item.case_index)
    : (approved?.cases.map((_, index) => index) ?? []);
  const catalog = useGenerationCatalogPage(
    approved ? `${approved.id}:${approved.version}` : null,
    catalogIndices,
  );
  // Still loading — render nothing rather than flash a placeholder.
  if (isLoading) return null;
  // Failed to load — say so rather than falsely claiming nothing was approved.
  if (isError) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-[12px] text-destructive">
          Couldn&apos;t load the generated cases. Refresh the page to try again.
        </CardContent>
      </Card>
    );
  }
  // Past review but nothing was approved (e.g. a failed/cancelled generation, or review never
  // completed) — show a small explanation instead of a blank area.
  if (!approved) {
    if (hideEmpty) return null;
    return (
      <Card>
        <CardContent className="py-6 text-center text-[12px] text-muted-foreground">
          No test cases have been saved for this run.
        </CardContent>
      </Card>
    );
  }

  const publications = approved.publications ?? [];
  const hasPublications = publications.length > 0;
  const total = hasPublications ? publications.length : approved.cases.length;
  // Backward-compat: older approved proposals predate `created_case_indices`. When it's absent but
  // an id exists for every case, treat all cases as created so the result view stays accurate.
  const createdIndices =
    approved.created_case_indices ??
    (approved.created_testrail_case_ids?.length === total
      ? approved.cases.map((_, i) => i)
      : []);
  const createdSet = new Set(createdIndices);
  // Legacy created_testrail_case_ids is aligned to the created cases in order, so the k-th created
  // index maps to the k-th id. Durable publication rows carry their own identity and never use this
  // fallback path.
  const idByIndex = new Map<number, string>();
  createdIndices.forEach((caseIdx, k) => {
    const id = approved.created_testrail_case_ids?.[k];
    if (id) idByIndex.set(caseIdx, id);
  });
  const createdCount = hasPublications
    ? publications.filter((publication) => publication.case_state === 'created')
        .length
    : createdSet.size;

  const storyLinkingAlert = storyLinkingAlertMessage(approved.story_linking);
  const approvedProposal = approved;

  function retryPublication(publication: GeneratedCasePublication): void {
    retry.mutate(
      { proposalId: approvedProposal.id, publicationId: publication.id },
      {
        onError: (error) =>
          notify.error(errMsg(error, 'Failed to retry story links')),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle
          ref={catalog.headingRef}
          tabIndex={-1}
          className="flex items-center gap-2"
        >
          <ListChecksIcon className="h-4 w-4" aria-hidden />
          Saved test cases
          <GenerationVersionBadge version={approved.version} />
          <Badge variant={createdCount > 0 ? 'success' : 'secondary'}>
            {createdCount} of {total} created
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {showGenerationMetadata ? (
          <GenerationMetaReview meta={approved.generationMeta} />
        ) : null}
        {storyLinkingAlert ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-info-500/30 bg-info-500/10 px-3 py-2 text-[12px] text-info-600"
          >
            <CircleAlertIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{storyLinkingAlert}</span>
          </div>
        ) : null}
        <GenerationCatalogPagination
          page={catalog.page}
          pageCount={catalog.pageCount}
          count={catalogIndices.length}
          onPage={catalog.navigate}
          label="Saved cases, top"
        />
        <ul className="divide-y divide-border">
          {hasPublications
            ? publications
                .filter((publication) =>
                  catalog.visibleIndices.includes(publication.case_index),
                )
                .map((publication) => {
                  const index = publication.case_index;
                  const c = publication.selected_case ??
                    approved.cases[index] ?? {
                      title: `Case #${index + 1}`,
                    };
                  return (
                    <ResultCaseRow
                      key={publication.id}
                      index={index}
                      open={catalog.isExpanded(index)}
                      onOpenChange={(open) => catalog.setExpanded(index, open)}
                      c={c}
                      executionContext={
                        approved.generationMeta?.frozenInput?.executionContext
                      }
                      publication={publication}
                      createdIdPrefix={createdIdPrefix(
                        source ?? publication.provider,
                      )}
                      canRetry={canRetryPublication(publication)}
                      retryPending={
                        retry.isPending &&
                        retry.variables?.publicationId === publication.id
                      }
                      onRetry={() => retryPublication(publication)}
                    />
                  );
                })
            : catalog.visibleIndices.map((i) => (
                <ResultCaseRow
                  key={`${approved.id}-${i}`}
                  index={i}
                  open={catalog.isExpanded(i)}
                  onOpenChange={(open) => catalog.setExpanded(i, open)}
                  c={approved.cases[i]!}
                  executionContext={
                    approved.generationMeta?.frozenInput?.executionContext
                  }
                  created={createdSet.has(i)}
                  createdId={idByIndex.get(i)}
                  createdIdPrefix={createdIdPrefix(source)}
                />
              ))}
        </ul>
        <GenerationCatalogPagination
          page={catalog.page}
          pageCount={catalog.pageCount}
          count={catalogIndices.length}
          onPage={catalog.navigate}
          label="Saved cases, bottom"
        />
      </CardContent>
    </Card>
  );
}

function ResultCaseRow({
  index,
  c,
  executionContext,
  created,
  createdId,
  createdIdPrefix,
  publication,
  canRetry = false,
  retryPending = false,
  onRetry,
  open,
  onOpenChange,
}: {
  index: number;
  c: ProposalCase;
  executionContext?: GenerationCredentialContext;
  created?: boolean;
  createdId?: string;
  createdIdPrefix: string;
  publication?: GeneratedCasePublication;
  canRetry?: boolean;
  retryPending?: boolean;
  onRetry?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const detailsId = `generated-case-${index}-details`;
  const identity = publication
    ? publicationIdentity(publication, publication.provider)
    : null;
  return (
    <li className="py-2">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-controls={open ? detailsId : undefined}
        className="flex min-h-8 w-full min-w-0 items-center gap-2 text-left"
      >
        <ChevronDownIcon
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`}
          aria-hidden
        />
        <span className="text-[10px] text-muted-foreground">#{index + 1}</span>
        <span className="min-w-0 flex-1 line-clamp-2 text-sm font-medium">
          {c.title}
        </span>
        {publication ? (
          <Badge
            variant={caseStateBadgeVariant(publication.case_state)}
            className="shrink-0 gap-1"
          >
            {publication.case_state === 'created' ? (
              <CheckCircle2Icon className="h-2.5 w-2.5" aria-hidden />
            ) : (
              <CircleAlertIcon className="h-2.5 w-2.5" aria-hidden />
            )}
            {caseStateLabel(publication.case_state)}
            {identity ? ` · ${identity}` : ''}
          </Badge>
        ) : created ? (
          <Badge variant="success" className="shrink-0 gap-1">
            <CheckCircle2Icon className="h-2.5 w-2.5" aria-hidden />
            {createdId ? `Created · ${createdIdPrefix}${createdId}` : 'Created'}
          </Badge>
        ) : (
          <Badge variant="secondary" className="shrink-0 gap-1">
            <MinusCircleIcon className="h-2.5 w-2.5" aria-hidden />
            Not created
          </Badge>
        )}
      </button>
      {publication ? (
        <PublicationOutcomeSummary
          publication={publication}
          canRetry={canRetry}
          retryPending={retryPending}
          onRetry={onRetry}
        />
      ) : null}
      {open && (
        <div
          id={detailsId}
          className="flex max-w-4xl flex-col gap-4 pb-3 pt-3 text-[13px] leading-relaxed sm:pl-9"
        >
          {c.precondition ? (
            <GenerationPreconditions
              text={c.precondition}
              context={executionContext}
            />
          ) : null}
          <GenerationCaseSteps testCase={c} context={executionContext} />

          <CaseCoverage c={c} />
        </div>
      )}
    </li>
  );
}

function PublicationOutcomeSummary({
  publication,
  canRetry,
  retryPending,
  onRetry,
}: {
  publication: GeneratedCasePublication;
  canRetry: boolean;
  retryPending: boolean;
  onRetry?: () => void;
}): JSX.Element | null {
  if (
    publication.provider === 'own-tms' &&
    !isPartialSuccess(publication) &&
    !publication.case_error_reason_code &&
    !publication.case_error_message &&
    publication.relation_outcomes.length === 0 &&
    !canRetry
  )
    return null;
  const linkByRef = new Map(
    publication.retained_links.map((link) => [link.ref, link]),
  );
  return (
    <div className="flex flex-col gap-1 px-3 py-2 text-[11px]">
      {isPartialSuccess(publication) ? (
        <div className="flex items-center gap-1.5 text-warning-600">
          <CircleAlertIcon className="size-3" aria-hidden />
          <span className="font-medium">Partial success</span>
          <span className="text-muted-foreground">
            — the case was created; some follow-up links or placement need
            attention.
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground">
        {publication.provider === 'own-tms' ? null : (
          <span>
            <span className="font-medium text-foreground">Placement:</span>{' '}
            {placementLabel(publication.placement_state)}
            {publication.placement_error_reason_code
              ? ` · ${publication.placement_error_reason_code}`
              : ''}
            {publication.placement_error_message
              ? ` · ${publication.placement_error_message}`
              : ''}
          </span>
        )}
        {publication.case_error_reason_code ||
        publication.case_error_message ? (
          <span>
            <span className="font-medium text-foreground">Creation:</span>{' '}
            {publication.case_error_reason_code}
            {publication.case_error_message
              ? ` · ${publication.case_error_message}`
              : ''}
          </span>
        ) : null}
        {publication.relation_outcomes.length > 0 ? (
          <span>
            <span className="font-medium text-foreground">Story links:</span>{' '}
            {
              publication.relation_outcomes.filter(
                (outcome) => outcome.state === 'succeeded',
              ).length
            }{' '}
            of {publication.relation_outcomes.length} linked
          </span>
        ) : null}
      </div>
      {publication.relation_outcomes.length > 0 ? (
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
          {publication.relation_outcomes.map((outcome) => {
            const link = linkByRef.get(outcome.ref);
            return (
              <li key={outcome.ref}>
                <span className="font-medium text-foreground">
                  {link?.key ?? outcome.ref}:
                </span>{' '}
                {relationLabel(outcome.state)}
                {outcome.reason_code ? ` · ${outcome.reason_code}` : ''}
              </li>
            );
          })}
        </ul>
      ) : null}
      {canRetry ? (
        <div>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
            onClick={onRetry}
            disabled={retryPending}
          >
            <RefreshCwIcon
              className={retryPending ? 'size-3 animate-spin' : 'size-3'}
              aria-hidden
            />
            {retryPending ? 'Retrying story links…' : 'Retry story links'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function errMsg(error: unknown, fallback: string): string {
  if (error instanceof QualityApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}
